import ConfigurationParser from './parsers/ConfigurationParser'
import TargetGroupRepository from './repositories/TargetGroupRepository'
import ExperimentsRepository from './repositories/ExperimentsRepository'
import FlagsRepository from './repositories/RoxFlagRepository'
import FlagsSetter from './setters/FlagsSetter'
import {flagValue} from './lib/RoxxOperators'

/**
 * Initialise CloudBees Feature Management.
 * Flag definitions will be read from the provided client data, then made available through the other
 * functions here.
 * To get a clientData, use the fetcher utility's #fetchClientData function, and then pass this (or some otherwise
 * pre-fetched and cached version) to this initialisation function.
 * The provided client data should have been fetched for the 'NodeJS' platform, and this is the platform name that will
 * be used by all utilities in this file.
 * Note that none of the other functions will return anything interesting or useful until this initialisation
 * has been called!
 *
 * @param clientData the clientData
 * @param envKey optionally, the environment key. If provided it will be validated that the clientData belongs to this
 *  environment. If it does not, an error is thrown.
 */
export const initFeatureFlags = (clientData, envKey = undefined) => {
  // If no envKey provided, pass dummy/fake environment ID and turn off validation of it.
  const parser = new ConfigurationParser(clientData, envKey || ' ', !!envKey)

  if (!parser.parse()) {
    throw new Error(
      `Environment key validation failed. Check that this client data belongs to environment key ${envKey}`
    )
  }
  TargetGroupRepository.setTargetGroups(parser.targetGroups())
  ExperimentsRepository.setExperiments(parser.experiments())

  new FlagsSetter(FlagsRepository, ExperimentsRepository).prepareFlagsWithExperiments()
}

/**
 * Get the names of all the feature flags that were in the clientData.
 * This will _only_ include flags that have targeting enabled, and as a result may be a subset of what you see in the
 * dashboard/API.
 * This will be 'none' until #initFeatureFlags() has been called and provided with a clientData.
 *
 * @return the names of all the flags in the clientData.
 */
export const getAllFlags = (): string[] => {
  // The _FlagsRepository_ only gets populated with flags declared locally, in the code. Those parsed from
  // clientData end up as experiments only. So return the names from the *Experiment* repository.
  const flagNames = []
  ExperimentsRepository.experiments.forEach((experiment) => {
    if (experiment.flags.length) {
      const flagName = experiment.flags[0].name
      // Filter out internal flags.
      if (flagName && !flagName.startsWith('rox.internal.')) {
        flagNames.push(flagName)
      }
    }
  })

  return flagNames
}

/**
 * Get the value of / evaluate a flag, returning a string value.
 *
 * @param flagName the name of the flag to evaluate
 * @param context the context in which the flag will be calculated.
 *  See https://docs.cloudbees.com/docs/cloudbees-feature-management/latest/feature-releases/flag-context
 */
export const getFlagValue = (flagName: string, context = {}): string => {
  return flagValue(flagName, context)
}

/**
 * Get the value of / evaluate a flag, returning a number value.
 * If used with a flag that contains a non-numeric value, returns NaN.
 *
 * @param flagName the name of the flag to evaluate
 * @param context the context in which the flag will be calculated.
 *  See https://docs.cloudbees.com/docs/cloudbees-feature-management/latest/feature-releases/flag-context
 */
export const getNumberFlagValue = (flagName: string, context = {}): number => {
  return Number(flagValue(flagName, context))
}

/**
 * Get the value of / evaluate a flag, returning a boolean value.
 * If used with a flag that contains a non-boolean value, it will return true only if the value of the flag is 'true'.
 *
 * @param flagName the name of the flag to evaluate
 * @param context the context in which the flag will be calculated.
 *  See https://docs.cloudbees.com/docs/cloudbees-feature-management/latest/feature-releases/flag-context
 */
export const isEnabled = (flagName: string, context = {}): boolean => {
  return getFlagValue(flagName, context).toLowerCase() === 'true'
}
